import { Injectable } from '@angular/core';
import {
    HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AuthenticationService } from '../services/auth.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private isRefreshing = false;
    private refreshSubject = new BehaviorSubject<string | null>(null);

    constructor(
        private authenticationService: AuthenticationService,
        private router: Router
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const currentUser = this.authenticationService.getLoggedUser();
        if (currentUser?.token) {
            request = this.attachToken(request, currentUser.token);
        }

        return next.handle(request).pipe(
            catchError(error => {
                if (error instanceof HttpErrorResponse && error.status === 401 && !request.url.includes('/auth/refresh')) {
                    return this.handle401(request, next);
                }
                return throwError(() => error);
            })
        );
    }

    private attachToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    private handle401(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (this.isRefreshing) {
            // Queue concurrent requests until refresh completes
            return this.refreshSubject.pipe(
                filter(token => token !== null),
                take(1),
                switchMap(token => next.handle(this.attachToken(request, token!)))
            );
        }

        this.isRefreshing = true;
        this.refreshSubject.next(null);

        return this.authenticationService.refreshToken().pipe(
            switchMap(newToken => {
                this.isRefreshing = false;
                this.refreshSubject.next(newToken);
                return next.handle(this.attachToken(request, newToken));
            }),
            catchError(err => {
                this.isRefreshing = false;
                this.authenticationService.logout();
                this.router.navigate(['/auth/login']);
                return throwError(() => err);
            })
        );
    }
}
